import { useMemo } from "react";

import { sortBy } from "lodash-es";
import { medianSorted } from "simple-statistics";

import { TerritoryLevel } from "@mapmycustomers/shared";
import AreaRegion from "@mapmycustomers/shared/types/territory/AreaRegion";
import AreaType from "@mapmycustomers/shared/types/territory/AreaType";

import { RegionDemographicData } from "@app/scene/map/store/layers/boundaries/types/BoundariesLayerData";
import DemographicData from "@app/scene/map/types/DemographicData";

const useAggregateDemographyData = (
  areaType: AreaType | undefined,
  regionIds: AreaRegion["id"][] | undefined,
  demographicData: RegionDemographicData | undefined
): Omit<DemographicData, "code" | "type"> | undefined => {
  const regionDemographyData = useMemo(() => {
    if (
      areaType &&
      regionIds &&
      demographicData &&
      ["US_adm1", "US_adm2", "US_adm3"].includes(areaType)
    ) {
      const regionsData = sortBy(
        regionIds.reduce((result: DemographicData[], regionId) => {
          if (areaType.includes(TerritoryLevel.ADM2) && demographicData.fips[regionId]) {
            return [...result, demographicData.fips[regionId]];
          } else if (areaType.includes(TerritoryLevel.ADM3) && demographicData.zip[regionId]) {
            return [...result, demographicData.zip[regionId]];
          } else if (areaType.includes(TerritoryLevel.ADM1) && demographicData.state[regionId]) {
            return [...result, demographicData.state[regionId]];
          }
          return result;
        }, []),
        ["income.value"]
      );

      const totalPopulation = regionsData.reduce(
        (result, { population }) => result + population,
        0
      );

      const medianIncome =
        regionsData.length > 0
          ? medianSorted(regionsData.map((data) => data.income.value))
          : undefined;
      return medianIncome
        ? {
            income: { currencyId: regionsData[0].income.currencyId, value: medianIncome },
            population: totalPopulation,
          }
        : undefined;
    }
    return undefined;
  }, [areaType, demographicData, regionIds]);

  return regionDemographyData;
};

export default useAggregateDemographyData;
